import dayjs from "dayjs";

// ============================================================================
// Resume Date Formatting Utilities
// ============================================================================

const ISO_DATE_PATTERN = /^\d{4}-\d{2}$/;
const ISO_RANGE_PATTERN = /^\d{4}-\d{2}\|(\d{4}-\d{2}|current)$/;

type ParsedResumeDate = {
  year: number;
  month: number;
};

type ParsedResumeDateValue = {
  isLegacy: boolean;
  startDate?: ParsedResumeDate;
  endDate?: ParsedResumeDate | "current";
  legacyText?: string;
};

/**
 * Parse a resume date value string (handles both ISO and legacy formats)
 * ISO formats: "2023-03", "2023-03|2024-01", "2023-03|current"
 * Legacy formats: "March 2023", "March 2023 - Present", etc.
 */
export function parseResumeDate(value: string): ParsedResumeDateValue {
  if (!value || value.trim() === "") {
    return { isLegacy: false };
  }

  const trimmed = value.trim();

  // Check for ISO single date: "2023-03"
  if (ISO_DATE_PATTERN.test(trimmed)) {
    const [year, month] = trimmed.split("-").map(Number);
    return {
      isLegacy: false,
      startDate: { year, month },
    };
  }

  // Check for ISO range: "2023-03|2024-01" or "2023-03|current"
  if (ISO_RANGE_PATTERN.test(trimmed)) {
    const [startStr, endStr] = trimmed.split("|");
    const [startYear, startMonth] = startStr.split("-").map(Number);
    const startDate: ParsedResumeDate = { year: startYear, month: startMonth };

    if (endStr === "current") {
      return {
        isLegacy: false,
        startDate,
        endDate: "current",
      };
    }

    const [endYear, endMonth] = endStr.split("-").map(Number);
    return {
      isLegacy: false,
      startDate,
      endDate: { year: endYear, month: endMonth },
    };
  }

  // Legacy format - return as-is
  return {
    isLegacy: true,
    legacyText: trimmed,
  };
}

/**
 * Format a resume date for display
 * Default format: MM/YYYY (e.g., 12/2020, 12/2020 - 10/2021, 12/2020 - Current)
 * Legacy values are returned as-is for backward compatibility
 */
export function formatResumeDate(value: string): string {
  if (!value) return "";

  const parsed = parseResumeDate(value);

  // Legacy format - return as-is
  if (parsed.isLegacy && parsed.legacyText) {
    return parsed.legacyText;
  }

  if (!parsed.startDate) {
    return "";
  }

  const formatDate = (date: ParsedResumeDate) => {
    return `${String(date.month).padStart(2, "0")}/${date.year}`;
  };

  const startFormatted = formatDate(parsed.startDate);

  if (!parsed.endDate) {
    return startFormatted;
  }

  if (parsed.endDate === "current") {
    return `${startFormatted} - Current`;
  }

  return `${startFormatted} - ${formatDate(parsed.endDate)}`;
}

// ============================================================================
// General Date Utilities
// ============================================================================

export const sortByDate = <T>(a: T, b: T, key: keyof T, desc = true) => {
  if (!a[key] || !b[key]) return 0;
  if (!(a[key] instanceof Date) || !(b[key] instanceof Date)) return 0;

  if (dayjs(a[key] as Date).isSame(dayjs(b[key] as Date))) return 0;
  if (desc) return dayjs(a[key] as Date).isBefore(dayjs(b[key] as Date)) ? 1 : -1;
  else return dayjs(a[key] as Date).isBefore(dayjs(b[key] as Date)) ? -1 : 1;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepSearchAndParseDates = (obj: any, dateKeys: string[]): any => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  const keys = Object.keys(obj);

  if (keys.length === 0) {
    return obj;
  }

  for (const key of keys) {
    let value = obj[key];

    if (dateKeys.includes(key) && typeof value === "string") {
      const parsedDate = new Date(value);
      if (!Number.isNaN(parsedDate.getTime())) {
        value = parsedDate;
      }
    }

    obj[key] = deepSearchAndParseDates(value, dateKeys);
  }

  return obj;
};
